import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Typography } from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { RadioOptions } from 'shared/ui/Radio'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { type ReleaseNumberStore } from '../store/ReleaseNumberStore'
import styles from './styles.module.scss'

type IReleaseNumberModalProps = {
  releaseNumberStoreStore: ReleaseNumberStore
}

export const ReleaseNumberModal = observer(
  ({ releaseNumberStoreStore }: IReleaseNumberModalProps) => {
    const {
      onReleaseNumberModalClose,
      onReleaseNumberModalSubmit,
      isReleaseNumberDisabled,
      currentPhoneNumber,
      removeOptions,
      onSetApproveCheckbox,
      onSetReleaseNumberOptionId,
      checkedReleaseNumberOptionId,
      removeApproveCheckbox,
      isAssigned,
      numbers,
      checkedNumber,
      onNumberCheck,
    } = releaseNumberStoreStore

    const [search, onSearch] = useState('')

    const filteredNumbers = useMemo(() => {
      if (!numbers) return null
      if (!search) return numbers
      const localSearch = search.replace(/[()\s\-]/g, '')
      return numbers.filter(({ number }) => number.includes(localSearch))
    }, [numbers, search, currentPhoneNumber])

    return (
      <div className={styles.root}>
        <Alert
          item={{
            type: 'alert',
            title: `Are you sure you want to release ${currentPhoneNumber}?`,
            desc: 'This action is destructive and cannot be undone.',
          }}
        />

        <div className={styles.content}>
          {isAssigned && filteredNumbers && (
            <SelectField
              size={'medium'}
              labelText={'Reassign conversations to'}
              placeholder={
                checkedNumber ? (
                  <div className={styles.inputPlaceholder}>
                    <Typography
                      className={styles.labelContent}
                      variant={'body-md-regular'}
                      ariaLabel={'numberPhone'}
                    >
                      {checkedNumber.formatted_number}
                    </Typography>
                    <Typography
                      className={styles.labelRight}
                      variant={'body-xxs-medium'}
                      ariaLabel={'numberDot'}
                      color={'var(--black-40)'}
                    >
                      &#x2022;
                    </Typography>
                    <Typography
                      className={styles.labelRight}
                      variant={'body-sm-regular'}
                      ariaLabel={'numberName'}
                      color={'tertiary'}
                      ellipsis
                      tooltipProps={{
                        label: checkedNumber.numberableName,
                        placement: 'top',
                      }}
                    >
                      {checkedNumber.numberableName}
                    </Typography>
                  </div>
                ) : undefined
              }
              items={filteredNumbers.map((number) => ({
                id: number.id,
                labelWidth: '200px',
                label: number.national_number,
                labelRight: (
                  <Typography
                    className={styles.labelRight}
                    variant={'body-sm-regular'}
                    ariaLabel={'email'}
                    color={'tertiary'}
                    ellipsis
                    tooltipProps={{
                      label: number.numberableName,
                      placement: 'top',
                    }}
                  >
                    {number.numberableName}
                  </Typography>
                ),
                labelContent: (
                  <Typography
                    className={styles.labelContent}
                    variant={'body-md-regular'}
                    ariaLabel={'displayInfo'}
                  >
                    {number.formatted_number}
                  </Typography>
                ),
              }))}
              value={checkedNumber?.id}
              setValue={onNumberCheck}
              selectProps={{
                placeholder: 'Select Number',
                dropdownProps: {
                  onChangeOpen: (status) => {
                    if (status && search) {
                      onSearch('')
                    }
                  },
                  margin: -40,
                  propsDropdownList: {
                    search,
                    onSearch,
                    width: 420,
                  },
                  placeholder: 'Select Number',
                },
              }}
            />
          )}
          <div className={styles.section}>
            <Label
              label={{
                text: 'What would you like to do with the number?',
              }}
              marginBottom={9}
            />

            <RadioOptions
              options={removeOptions}
              active={checkedReleaseNumberOptionId}
              onChange={onSetReleaseNumberOptionId}
            />
          </div>

          <label className={styles.wrapPlaceholder} htmlFor={'releaseNumberCheckbox'}>
            <Checkbox
              id={'releaseNumberCheckbox'}
              checked={removeApproveCheckbox}
              onChange={onSetApproveCheckbox}
              size={16}
              style={{ cursor: 'pointer' }}
            />
            <Typography
              variant={'text-input-field'}
              color={'tertiary'}
              ariaLabel={'remove_understanding'}
            >
              I understand the action is destructive and can not be undone
            </Typography>
          </label>
        </div>

        <div className={styles.actions}>
          <Button
            typeBtn={'contained'}
            text={'Cancel'}
            size={'medium'}
            contained={'secondary'}
            textClassname={styles.button}
            onClick={onReleaseNumberModalClose}
          />
          <Button
            disabled={isReleaseNumberDisabled}
            typeBtn={'contained'}
            text={'Release number'}
            size={'medium'}
            textClassname={styles.button}
            onClick={onReleaseNumberModalSubmit}
          />
        </div>
      </div>
    )
  }
)
